/**
 * Email Log Panel Component
 * Colaiste Lurgan Management System — Read-only email history for a student
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Mail, AlertCircle, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';

// Status badge colour classes — light + dark mode
const STATUS_COLORS = {
  SENT:      'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/50 dark:text-blue-300 dark:border-blue-700',
  DELIVERED: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/50 dark:text-green-300 dark:border-green-700',
  OPENED:    'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/50 dark:text-emerald-300 dark:border-emerald-700',
  PENDING:   'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/50 dark:text-amber-300 dark:border-amber-700',
  FAILED:    'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/50 dark:text-red-300 dark:border-red-700',
  BOUNCED:   'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/50 dark:text-red-300 dark:border-red-700',
  DRAFT:     'bg-muted text-muted-foreground border-border',
};

function formatDateTime(dateString) {
  if (!dateString) return '—';
  return new Intl.DateTimeFormat('en-IE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(dateString));
}

export function EmailLogPanel({ student }) {
  const { t } = useTranslation();

  const { data, isLoading, error } = useQuery({
    queryKey: ['student-email-history', student?.uuid],
    queryFn: () => api.emails.getStudentHistory(student.uuid),
    enabled: !!student?.uuid,
  });

  const emails = data?.email_history ?? [];

  return (
    <Card>
      <CardHeader className="bg-muted/40 border-b border-border py-3 px-4">
        <CardTitle className="flex items-center text-sm font-semibold text-foreground">
          <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#F28482]/10 mr-3">
            <Mail className="h-4 w-4 text-[#F28482]" />
          </span>
          {t('emailLog.title')}
        </CardTitle>
      </CardHeader>

      <CardContent className="pt-4 px-0 pb-0">
        {isLoading && (
          <div className="flex items-center gap-2 px-4 pb-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{t('common.loading')}</span>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 px-4 pb-4 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{t('common.errorLoading')}</span>
          </div>
        )}

        {!isLoading && !error && emails.length === 0 && (
          <p className="px-4 pb-4 text-sm text-muted-foreground">
            {t('emailLog.noEmails')}
          </p>
        )}

        {!isLoading && !error && emails.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/20">
                  <th className="px-4 py-2 text-left font-medium text-muted-foreground whitespace-nowrap">{t('emailLog.columns.sentAt')}</th>
                  <th className="px-4 py-2 text-left font-medium text-muted-foreground">{t('emailLog.columns.subject')}</th>
                  <th className="px-4 py-2 text-left font-medium text-muted-foreground whitespace-nowrap hidden lg:table-cell">{t('emailLog.columns.parentEmail')}</th>
                  <th className="px-4 py-2 text-left font-medium text-muted-foreground">{t('emailLog.columns.status')}</th>
                  <th className="px-4 py-2 text-left font-medium text-muted-foreground whitespace-nowrap hidden lg:table-cell">{t('emailLog.columns.sentBy')}</th>
                </tr>
              </thead>
              <tbody>
                {emails.map((email) => (
                  <tr key={email.uuid} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap text-xs">
                      {formatDateTime(email.sent_at)}
                    </td>
                    <td className="px-4 py-2.5 max-w-xs truncate" title={email.subject}>
                      {email.subject || '—'}
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground hidden lg:table-cell text-xs">
                      {email.recipient_email || '—'}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[email.status] ?? STATUS_COLORS.DRAFT}`}>
                        {t(`emailLog.status.${email.status}`, email.status)}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground hidden lg:table-cell whitespace-nowrap">
                      {email.sent_by_name || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
